import React, { useState, useEffect } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { AuthLayout, Button } from "../components/common";
import { useAuth } from "../contexts/AuthContext";

const EmailVerification = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const {
    verifyEmail,
    resendVerification,
    isLoading,
    error,
    success,
    user,
  } = useAuth();

  const [verificationStatus, setVerificationStatus] = useState<
    "verifying" | "success" | "error" | "resend"
  >("verifying");

  useEffect(() => {
    const tokenFromUrl = searchParams.get("token");
    if (tokenFromUrl) {
      handleVerification(tokenFromUrl);
    } else {
      setVerificationStatus("resend");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const handleVerification = async (verificationToken: string) => {
    try {
      await verifyEmail(verificationToken);
      setVerificationStatus("success");
      setTimeout(() => {
        navigate("/");
      }, 3000);
    } catch (error) {
      setVerificationStatus("error");
    }
  };

  const handleResendVerification = async () => {
    if (user?.email) {
      try {
        await resendVerification({ email: user.email });
        setVerificationStatus("resend");
      } catch (error) {
        console.error("Failed to resend verification:", error);
      }
    }
  };

  const subtitle = (
    <>
      Need help?{" "}
      <Link
        to="/signin"
        className="font-medium text-cyan-500 hover:text-cyan-400 transition-colors duration-200"
      >
        Back to sign in
      </Link>
    </>
  );

  if (verificationStatus === "success" || success) {
    return (
      <AuthLayout title="Email verified successfully" subtitle={subtitle}>
        <div className="text-center">
          <div className="mx-auto flex items-center justify-center w-12 h-12 rounded-full bg-green-100 mb-4">
            <svg
              className="w-6 h-6 text-green-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Email verified successfully!
          </h3>
          <p className="text-gray-600 mb-6">
            Your email has been verified. You can now access all features of
            your account.
          </p>
          <Link to="/">
            <Button variant="primary" fullWidth>
              Continue to dashboard
            </Button>
          </Link>
        </div>
      </AuthLayout>
    );
  }

  if (verificationStatus === "error") {
    return (
      <AuthLayout title="Verification failed" subtitle={subtitle}>
        <div className="text-center">
          <div className="mx-auto flex items-center justify-center w-12 h-12 rounded-full bg-red-100 mb-4">
            <svg
              className="w-6 h-6 text-red-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Verification failed
          </h3>
          <p className="text-gray-600 mb-6">
            {error ||
              "The verification link is invalid or has expired. Please request a new verification email."}
          </p>
          <div className="space-y-3">
            <Button
              onClick={handleResendVerification}
              variant="primary"
              fullWidth
              isLoading={isLoading}
              disabled={isLoading || !user?.email}
            >
              {isLoading ? "Sending..." : "Send new verification email"}
            </Button>
            <Link to="/signin">
              <Button variant="outline" fullWidth>
                Back to sign in
              </Button>
            </Link>
          </div>
        </div>
      </AuthLayout>
    );
  }

  if (verificationStatus === "verifying") {
    return (
      <AuthLayout title="Verifying your email" subtitle={subtitle}>
        <div className="text-center">
          <div className="mx-auto flex items-center justify-center w-12 h-12 rounded-full bg-cyan-100 mb-4">
            <svg
              className="animate-spin w-6 h-6 text-cyan-600"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Verifying your email...
          </h3>
          <p className="text-gray-600 mb-6">
            Please wait while we verify your email address.
          </p>
        </div>
      </AuthLayout>
    );
  }

  // Resend verification state
  return (
    <AuthLayout title="Verify your email" subtitle={subtitle}>
      <div className="text-center">
        <div className="mx-auto flex items-center justify-center w-12 h-12 rounded-full bg-yellow-100 mb-4">
          <svg
            className="w-6 h-6 text-yellow-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.992-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
            />
          </svg>
        </div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">
          Please verify your email
        </h3>
        <p className="text-gray-600 mb-6">
          We've sent a verification email to your registered email address.
          Please check your inbox and click the verification link.
        </p>

        {success && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
            <p className="text-green-600 text-sm">{success}</p>
          </div>
        )}

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-600 text-sm">{error}</p>
          </div>
        )}

        <div className="space-y-3">
          <Button
            onClick={handleResendVerification}
            variant="primary"
            fullWidth
            isLoading={isLoading}
            disabled={isLoading || !user?.email}
          >
            {isLoading ? "Sending..." : "Resend verification email"}
          </Button>
          <Link to="/signin">
            <Button variant="outline" fullWidth>
              Back to sign in
            </Button>
          </Link>
        </div>
      </div>
    </AuthLayout>
  );
};

export default EmailVerification;
